/**
 * Has the working data moved since the agent last mapped it, and what should happen on arrival at
 * Map & review?
 *
 * One pure module, and one decision function rather than a pair of effects, because the seeding rule and
 * the mapping rule have to agree about the same inputs — two effects would agree only by accident of the
 * order React happens to run them in.
 */

import { isOutputKey } from "./outputFrames";
import { STAGE_LIVE } from "./stages";

// The destination task's own lookup tables. Same rule as WorkingPanel's `isReferenceFrame`, keyed on
// `origin` and not on the "task_" name prefix, because a user may name their own frame `task_notes`.
const TASK_REFERENCE_ORIGIN = "task_reference";

/** Is this one of the frames the user is transforming (as opposed to a mapped result or a lookup table)? */
export function isWorkingFrame(df) {
  return Boolean(df) && !isOutputKey(df.key) && df.origin !== TASK_REFERENCE_ORIGIN;
}

/**
 * A stable token for "the state of the working data", from the frame list alone.
 *
 * `fingerprint` comes from `ds_session_frame` and changes only when a frame's CONTENT changed
 * (`put_frame` short-circuits an identical write). Row and column counts are the tempting alternative and
 * are wrong: the commonest transform in this tool is a value mapping, which rewrites cells and leaves the
 * shape untouched.
 *
 * Output frames are excluded, and that is load-bearing rather than tidy: a mapping run WRITES an output
 * frame, so a signature that counted them would report the working data as having changed every single
 * time a mapping succeeded — and the next arrival at Map & review would map again, for ever.
 *
 * Sorted by key so the same set of frames in a different server order is the same signature. Returns ""
 * for an empty list, which callers must treat as "nothing loaded yet" rather than as a real state — see
 * `isListReady` in `decideMapping`.
 */
export function workingDataSignature(dataframes) {
  return (dataframes || [])
    .filter(isWorkingFrame)
    .map((d) => `${d.key}:${d.fingerprint ?? ""}`)
    .sort()
    .join("|");
}

/** Does the session have a mapped output frame to review? */
export function hasOutputFrame(dataframes) {
  return (dataframes || []).some((d) => isOutputKey(d.key) && d.origin !== TASK_REFERENCE_ORIGIN);
}

/**
 * What to do about the mapping, given everything that bears on it.
 *
 *  - "map"   — start a mapping turn now.
 *  - "seed"  — record the current signature as already-mapped without running anything.
 *  - "wait"  — the answer is "map", but not yet; ask again when the inputs change.
 *  - "skip"  — nothing to do.
 *
 * @param {object}  a
 * @param {string}  a.stage             Current pipeline stage.
 * @param {string}  a.signature         `workingDataSignature(dataframes)`.
 * @param {?string} a.mappedSignature   The signature the existing output was built from, or null.
 * @param {boolean} a.hasOutput         Is there an output frame at all?
 * @param {boolean} a.isListReady       Has the frame list actually loaded?
 * @param {boolean} a.isFoundation      A Foundation Data v2 session (it has no Map & review stage).
 * @param {boolean} a.hasDestination    Does the session know what it is mapping onto?
 * @param {boolean} a.isConnected       Is the websocket up?
 * @param {boolean} a.isTurnActive      Is the agent mid-turn?
 * @param {boolean} a.force             The user pressed "Re-map with AI".
 */
export function decideMapping({
  stage,
  signature,
  mappedSignature,
  hasOutput,
  isListReady,
  isFoundation,
  hasDestination,
  isConnected,
  isTurnActive,
  force = false,
}) {
  // A foundation session's destination has the same shape as its source, so it has no Map & review stage
  // at all (WorkingPanel omits the tab) and nothing to map.
  if (isFoundation) return "skip";

  // Reopening a session that already has an output: the output is taken to match the current working
  // data, so arriving at Map & review shows it instead of spending a multi-minute turn rebuilding
  // something the user may already have reviewed. Checked before the stage, so a session resumed
  // straight onto another stage is seeded too and does not map the first time the user looks.
  if (isListReady && mappedSignature === null && hasOutput && !force) return "seed";

  if (stage !== STAGE_LIVE) return "skip";

  // Without a destination the turn can only end in "which task am I mapping onto?" — a question that
  // cannot be answered from this stage, because the composer is locked on it.
  if (!hasDestination) return "skip";

  // Nothing has changed since the output was built. The whole point of the signature.
  if (!force && signature === mappedSignature) return "skip";

  // "wait", not "skip": an arrival that lands before the frame list has loaded, or while the agent is
  // still working, is still an arrival. Skipping would drop the request on the floor and the stage would
  // sit there doing nothing, which is exactly what it did before this feature.
  if (!isListReady || !isConnected || isTurnActive) return "wait";

  return "map";
}
